import Model, { Integer, IsoDateTime } from 'src/models/model'

export const DATA_PRESENCE = {
  NONE: 'none',
  SOME: 'some',
  COMPLETE: 'complete'
}

export const DATA_FRESHNESS = {
  NO_DATA: 'no-data',
  FINAL: 'final',
  PARTIAL: 'partial',
  OUTDATED: 'outdated',
  MIXED: 'mixed',
  DIRTY: 'dirty'
}

export const BUILD_STATUS = {
  CREATED: 'created',
  AWAITING_SCHEDULING: 'awaiting-scheduling',
  SCHEDULING: 'scheduling',
  AWAITING_ENQUEUEING: 'awaiting-enqueueing',
  ENQUEUEING: 'enqueueing',
  WAITING: 'waiting',
  BUILDING: 'building',
  DONE: 'done',
  ERROR: 'error'
}

export const WAITING_STATES = [
  BUILD_STATUS.CREATED,
  BUILD_STATUS.AWAITING_SCHEDULING,
  BUILD_STATUS.SCHEDULING,
  BUILD_STATUS.AWAITING_ENQUEUEING,
  BUILD_STATUS.ENQUEUEING,
  BUILD_STATUS.WAITING
]

const BUILD_BUFFER_TIME_MS = 1000 * 10

export default class ReportStatus extends Model {
  static name = 'ReportStatus'

  static noId = true

  static define () {
    this.fields = {
      dataPresence: {
        type: String
      },
      dataFreshness: {
        type: String
      },
      buildStatus: {
        type: String
      },
      errorMessage: {
        type: String
      },

      queuePosition: {
        type: Integer
      },
      estimatedTimeUntilStartMs: {
        type: Integer
      },
      estimatedBuildTimeRemainingMs: {
        type: Integer
      },

      waitingPercentComplete: {
        type: Integer
      },
      buildPercentComplete: {
        type: Integer
      },
      overallPercentComplete: {
        type: Integer
      },

      buildTimeMs: {
        type: Integer
      },
      buildStartedAt: {
        type: IsoDateTime
      },
      lastVerifiedAt: {
        type: IsoDateTime
      },
      lastComputedAt: {
        type: IsoDateTime
      }
    }
  }

  estimatedTimeRemaining (options = { compact: true, duration: true, allowNegativeDuration: false }) {
    if (!this.lastVerifiedAt) return

    let time

    if (this.state === BUILD_STATUS.BUILDING) {
      time = new IsoDateTime(
        (new Date()).getTime() +
          (this.estimatedTimeUntilStartMs || 0) +
          (this.estimatedBuildTimeRemainingMs || 0) +
          BUILD_BUFFER_TIME_MS
      )
    } else {
      time = new IsoDateTime(
        this.lastVerifiedAt.getTime() +
          (this.estimatedTimeUntilStartMs || 0) +
          (this.estimatedBuildTimeRemainingMs || 0) +
          BUILD_BUFFER_TIME_MS
      )
    }

    return time.relativeTime(options)
  }

  estimatedTimeUntilStart (options = { compact: true, duration: true, allowNegativeDuration: false }) {
    if (!this.lastVerifiedAt) return

    const time = new IsoDateTime(
      this.lastVerifiedAt.getTime() +
        (this.estimatedTimeUntilStartMs || 0) +
        BUILD_BUFFER_TIME_MS
    )
    return time.relativeTime(options)
  }

  estimatedBuildTimeRemaining (options = { compact: true, duration: true, allowNegativeDuration: false }) {
    const time = new IsoDateTime(
      (new Date()).getTime() +
        (this.estimatedBuildTimeRemainingMs || 0) +
        BUILD_BUFFER_TIME_MS
    )
    return time.relativeTime(options)
  }

  buildTime (options = { compact: true, duration: true }) {
    this.buildTimeBasis = this.buildTimeBasis || new Date()
    const time = new IsoDateTime(
      this.buildTimeBasis.getTime() +
        (this.buildTimeMs || 0)
    )
    return time.relativeTime({ ...options, relativeTo: this.buildTimeBasis })
  }

  get waiting () {
    return _.includes(WAITING_STATES, this.buildStatus)
  }

  get building () {
    return this.buildStatus === BUILD_STATUS.BUILDING
  }

  get done () {
    return this.buildStatus === BUILD_STATUS.DONE
  }

  get noData () {
    return this.dataPresence === DATA_PRESENCE.NONE
  }

  get error () {
    return this.buildStatus === BUILD_STATUS.ERROR
  }
}
